/**
 * ApBaselineCard -- "AP baseline" on the AP device sheet: status, preview diff, apply.
 *
 * WHY (owner-safety, 2026-10-12, OP#1775): the baseline (TIP factory routed LAN -> bridged onto
 * the uplink; 6 GHz dropped from Open/WPA2 SSIDs the AP refuses) used to ride every SSID / radio
 * push, so one SSID edit would have bridged every AP at a site at once. It is now applied ONLY
 * here, one AP at a time:
 *   - status comes from GET /devices/{serial}/baseline (read-only, visible to every role);
 *   - "Preview" runs POST .../baseline/plan (org_admin+; writes nothing) and shows the
 *     before -> after of the baseline fields plus the raw path diff (everything else is
 *     byte-identical, enforced server-side);
 *   - "Apply" needs the plan token AND an explicit "~4 minute outage" acknowledgement; the
 *     server refuses (409) when the AP is offline, config-incompatible, or a push is in flight.
 *   - WIRED CLIENTS (BasementTV, 2026-10-13): when the bridge strands wired clients on the LAN
 *     ports (they keep the AP's old private lease; the PHY does not flap), the plan lists them
 *     and a SECOND acknowledgement names every one; their MACs go back on apply and the server
 *     refuses an apply that does not name them all (409 wired_clients_not_acknowledged).
 *   - ROBUSTNESS: status / plan bodies pass through normalizeBaselineStatus / -Plan first; a
 *     body missing `lan` (or not an object) renders "Unavailable" instead of crashing the sheet.
 * A model whose firmware rejected the shared-uplink shape (EAP111) shows "Not supported on this
 * model" for the LAN and is never offered the bridge.
 * Role: org_admin (api/v2/ap_baseline.BASELINE_WRITE_ROLE) -- RoleGate disables, never hides.
 */
import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  useApBaselineStatus, useApplyApBaseline, usePlanApBaseline, type ApBaselinePlan,
} from "@/lib/api/apBaseline";
import {
  STATE_LABEL, UNAVAILABLE, applyResultView, lanLabel, normalizeBaselinePlan,
  normalizeBaselineStatus, stateTone, summaryLines, wiredAckText,
} from "@/lib/apBaselineView";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { ErrorBanner } from "@/components/ui/Page";
import { RoleGate } from "@/components/auth/RoleGate";

/** Must match api/v2/ap_baseline.BASELINE_WRITE_ROLE. */
const BASELINE_ROLE = "org_admin" as const;
const ROLE_REASON = "Re-applying the AP baseline requires a customer admin.";

export function ApBaselineCard({ serial }: { serial: string }) {
  const status = useApBaselineStatus(serial);
  const [open, setOpen] = useState(false);
  // Guarded view of the body: null = not an object at all; `missing` = blocks it lacked.
  const norm = useMemo(
    () => (status.data === undefined ? undefined : normalizeBaselineStatus(status.data)),
    [status.data],
  );
  const st = norm?.status;
  const lanMissing = !!norm?.missing.includes("lan");
  const sixMissing = !!norm?.missing.includes("six_ghz");

  return (
    <div className="md-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-ink">AP baseline</span>
        {st && <StatusPill status={stateTone(st.state)}>{STATE_LABEL[st.state]}</StatusPill>}
        {st?.unconfirmed_push && <StatusPill status="info">Waiting for the AP to confirm</StatusPill>}
        {status.isPending && <span className="text-xs text-ink-muted">Reading the AP's configuration…</span>}
        <div className="ml-auto">
          <RoleGate min={BASELINE_ROLE} mode="disable" reason={ROLE_REASON}>
            <Button variant="secondary" disabled={!st || st.state === "unknown"} onClick={() => setOpen(true)}>
              Preview / apply
            </Button>
          </RoleGate>
        </div>
      </div>
      {status.isError && (
        <div className="mt-2">
          <ErrorBanner error={status.error} />
        </div>
      )}
      {norm === null && (
        <p className="mt-2 text-xs text-ink-muted" data-testid="ap-baseline-unavailable">
          AP baseline status is unavailable: the controller's response was not readable.
        </p>
      )}
      {st && (
        <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
          <dt className="text-ink-muted">LAN ports</dt>
          <dd className="text-ink" data-testid="ap-baseline-lan">
            {lanMissing ? UNAVAILABLE : lanLabel(st.lan.state)}
            {st.lan.unsupported_reason && (
              <div className="text-ink-muted">{st.lan.unsupported_reason}</div>
            )}
          </dd>
          <dt className="text-ink-muted">6 GHz</dt>
          <dd className="text-ink">
            {sixMissing
              ? UNAVAILABLE
              : st.six_ghz.trim.length
                ? `Open/WPA2 SSIDs on 6 GHz (refused by the AP): ${st.six_ghz.trim.join(", ")}`
                : "No Open/WPA2 SSID on 6 GHz"}
          </dd>
          {st.config_incompatible && (
            <>
              <dt className="text-ink-muted">Config</dt>
              <dd className="text-ink">Excluded from Meridian config -- the baseline cannot be applied.</dd>
            </>
          )}
        </dl>
      )}
      {open && <ApBaselineDialog serial={serial} onClose={() => setOpen(false)} />}
    </div>
  );
}

function ApBaselineDialog({ serial, onClose }: { serial: string; onClose: () => void }) {
  const plan = usePlanApBaseline(serial);
  const apply = useApplyApBaseline(serial);
  const [ack, setAck] = useState(false);
  const [wiredAck, setWiredAck] = useState(false);

  // Preview once on open. Read-only on the server.
  const { mutate: runPlan } = plan;
  useEffect(() => {
    runPlan();
  }, [runPlan]);

  // Guarded plan: every list the dialog maps over is an array (normalizeBaselinePlan).
  const planned: ApBaselinePlan | null | undefined = useMemo(
    () => (plan.data === undefined ? undefined : normalizeBaselinePlan(plan.data)),
    [plan.data],
  );
  const done = apply.data;
  const wiredText = planned ? wiredAckText(planned) : null;
  const wiredMacs = planned?.wired_clients?.clients.map((c) => c.mac) ?? [];

  return (
    <Dialog.Root open onOpenChange={(o) => !o && !apply.isPending && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Re-apply AP baseline</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Only {serial} is touched. Nothing has been sent yet -- this is the exact change; every other
            setting stays byte-identical.
          </Dialog.Description>

          {plan.isPending && <div className="mt-4 text-sm text-ink-muted">Working out what would change…</div>}
          {plan.isError && (
            <div className="mt-4">
              <ErrorBanner error={plan.error} />
            </div>
          )}
          {planned === null && (
            <div className="mt-4 text-sm text-ink-muted">
              The preview is unavailable: the controller's response was not readable. Nothing was sent.
            </div>
          )}

          {planned && !done && (
            <div className="mt-4 flex flex-col gap-3">
              {planned.changed ? (
                <div className="md-card p-3 text-sm">
                  <div className="font-medium text-ink">What changes</div>
                  <ul className="mt-1 list-disc pl-5 text-ink">
                    {summaryLines(planned.before, planned.after).map((l) => (
                      <li key={l}>{l}</li>
                    ))}
                  </ul>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-ink-muted">
                      Raw diff ({planned.diff.length} path{planned.diff.length === 1 ? "" : "s"})
                    </summary>
                    <ul className="mt-1 max-h-48 overflow-y-auto font-mono text-[0.6875rem]">
                      {planned.diff.map((d, i) => (
                        <li key={`${d.path}-${i}`} className="break-all">
                          {d.op} {d.path}
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              ) : (
                <div className="md-card p-3 text-sm text-ink-muted">
                  This AP already carries the baseline -- there is nothing to push.
                </div>
              )}

              {planned.warnings.map((w) => (
                <div key={w} role="note" className="md-card border-warn/40 bg-warn-soft p-3 text-sm text-ink">
                  {w}
                </div>
              ))}

              {planned.refusals
                .filter((r) => r.code !== "nothing_to_apply")
                .map((r) => (
                  <div key={r.code} className="md-card border-critical/40 bg-critical-soft p-3 text-sm text-ink">
                    {r.message}
                  </div>
                ))}

              {planned.can_apply && (
                <label className="md-card flex items-start gap-2 p-3 text-sm">
                  <input type="checkbox" className="mt-1" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                  <span>
                    I understand this AP goes offline for about {planned.outage_minutes} minutes while it
                    re-applies its configuration.
                  </span>
                </label>
              )}

              {/* Wired clients the bridge strands: named one by one; the server refuses an
                  apply whose acknowledgement does not list every MAC on those ports. */}
              {planned.can_apply && wiredText && (
                <label
                  className="md-card flex items-start gap-2 border-warn/40 p-3 text-sm"
                  data-testid="ap-baseline-wired-ack"
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={wiredAck}
                    onChange={(e) => setWiredAck(e.target.checked)}
                  />
                  <span>{wiredText}</span>
                </label>
              )}

              {apply.isError && <ErrorBanner error={apply.error} />}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={onClose} disabled={apply.isPending}>
                  Cancel
                </Button>
                <RoleGate min={BASELINE_ROLE} mode="disable" reason={ROLE_REASON}>
                  <Button
                    variant="danger"
                    disabled={!planned.can_apply || !ack || (!!wiredText && !wiredAck) || apply.isPending}
                    onClick={() => apply.mutate({ planToken: planned.plan_token, wiredMacs })}
                  >
                    {apply.isPending ? "Pushing…" : "Apply to this AP"}
                  </Button>
                </RoleGate>
              </div>
            </div>
          )}

          {done && (
            <div className="mt-4 flex flex-col gap-3">
              {(() => {
                // A timed-out push is "Waiting for the AP to confirm", never "failed" (OP#1775).
                const view = applyResultView(done);
                const box =
                  view.tone === "info"
                    ? "border-info/40 bg-info-soft"
                    : view.tone === "warn"
                      ? "border-warn/40 bg-warn-soft"
                      : "border-healthy/40 bg-healthy-soft";
                return (
                  <div role="status" className={`md-card ${box} p-4 text-sm`}>
                    <div className="font-medium text-ink">{view.title}</div>
                    <div className="mt-1 text-ink-muted">{view.text}</div>
                    {/* Wired clients the operator acknowledged lose their address (OP#1812). */}
                    {wiredMacs.length > 0 && (
                      <div className="mt-1 text-ink">
                        Once it is back, replug or power-cycle: {wiredMacs.join(", ")}.
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="flex justify-end">
                <Button variant="primary" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
